import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowLeft } from 'lucide-react';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="container mx-auto px-4 py-6 border-b">
        <nav className="flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80">
            <Sparkles className="w-8 h-8 text-blue-600" />
            <span className="text-xl font-bold">AI Marketing Engine</span>
          </button>
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </nav>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-3xl font-bold mb-8">Conditions Générales d'Utilisation</h1>
        <p className="text-slate-600 mb-8">Dernière mise à jour : Janvier 2025</p>

        <div className="prose prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold mb-4">1. Objet</h2>
            <p className="text-slate-600 leading-relaxed">
              Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation du service 
              AI Marketing Engine, une plateforme de marketing automatisé par intelligence artificielle 
              permettant la création et la publication de contenus sur différentes plateformes sociales et digitales.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">2. Acceptation des conditions</h2>
            <p className="text-slate-600 leading-relaxed">
              En créant un compte et en utilisant nos services, vous acceptez d'être lié par les présentes CGU. 
              Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser notre service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">3. Description du service</h2>
            <p className="text-slate-600 leading-relaxed mb-4">AI Marketing Engine propose :</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600">
              <li>Analyse automatique de votre site web par intelligence artificielle</li>
              <li>Génération de contenus textuels et visuels par IA</li>
              <li>Planification et publication automatique sur vos réseaux sociaux</li>
              <li>Tableau de bord de suivi et de validation</li>
              <li>Gestion multi-plateformes (X, Facebook, Instagram, LinkedIn, Telegram, Email, etc.)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">4. Contenus générés par IA</h2>
            <p className="text-slate-600 leading-relaxed">
              <strong>Avertissement important :</strong> Les contenus (textes, images) proposés par notre service sont 
              générés par intelligence artificielle. Bien que nous nous efforcions de produire des contenus de qualité :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600 mt-4">
              <li>Vous êtes seul responsable de la validation et de la publication finale des contenus</li>
              <li>Vous devez vérifier l'exactitude et la pertinence des contenus avant publication</li>
              <li>Nous ne garantissons pas que les contenus générés sont exempts d'erreurs ou d'inexactitudes</li>
              <li>L'utilisation des contenus générés relève de votre entière responsabilité</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">5. Inscription et compte</h2>
            <p className="text-slate-600 leading-relaxed">
              L'accès au service nécessite la création d'un compte et la souscription à un abonnement payant. 
              Vous vous engagez à :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600 mt-4">
              <li>Fournir des informations exactes et à jour</li>
              <li>Maintenir la confidentialité de vos identifiants de connexion</li>
              <li>Nous informer immédiatement de toute utilisation non autorisée de votre compte</li>
              <li>Ne pas partager votre compte avec des tiers</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">6. Abonnement et paiement</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Le service est accessible par abonnement mensuel. Les paiements sont traités de manière sécurisée par Stripe.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600">
              <li>Les prix sont indiqués en euros TTC</li>
              <li>L'abonnement est renouvelé automatiquement chaque mois</li>
              <li>Vous pouvez annuler à tout moment, l'accès reste actif jusqu'à la fin de la période payée</li>
              <li>Aucun remboursement n'est effectué pour les périodes entamées</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">7. Utilisation acceptable</h2>
            <p className="text-slate-600 leading-relaxed mb-4">Vous vous engagez à ne pas utiliser le service pour :</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600">
              <li>Créer ou diffuser des contenus illégaux, diffamatoires, haineux ou discriminatoires</li>
              <li>Violer les droits de propriété intellectuelle de tiers</li>
              <li>Diffuser du spam ou des contenus trompeurs</li>
              <li>Tenter de contourner les limitations techniques du service</li>
              <li>Utiliser le service d'une manière qui viole les conditions d'utilisation des plateformes tierces</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">8. Propriété intellectuelle</h2>
            <p className="text-slate-600 leading-relaxed">
              <strong>Contenus générés :</strong> Les contenus créés par l'IA pour votre compte vous appartiennent. 
              Vous en êtes propriétaire et pouvez les utiliser librement.
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              <strong>Service :</strong> AI Marketing Engine, son interface, son code et ses algorithmes restent 
              notre propriété exclusive. Toute reproduction ou utilisation non autorisée est interdite.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">9. Limitation de responsabilité</h2>
            <p className="text-slate-600 leading-relaxed">
              Dans les limites permises par la loi :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600 mt-4">
              <li>Le service est fourni "en l'état" sans garantie de résultat</li>
              <li>Nous ne sommes pas responsables des dommages indirects liés à l'utilisation du service</li>
              <li>Notre responsabilité est limitée au montant des sommes versées au cours des 12 derniers mois</li>
              <li>Nous ne sommes pas responsables des problèmes liés aux plateformes tierces (Facebook, X, etc.)</li>
              <li>Vous êtes seul responsable du contenu que vous publiez via notre service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">10. Données personnelles</h2>
            <p className="text-slate-600 leading-relaxed">
              Le traitement de vos données personnelles est régi par notre <a href="/privacy-policy" className="text-blue-600 hover:underline">Politique de Confidentialité</a>, 
              qui fait partie intégrante des présentes CGU.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">11. Suspension et résiliation</h2>
            <p className="text-slate-600 leading-relaxed">
              Nous nous réservons le droit de suspendre ou résilier votre compte en cas de :
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600 mt-4">
              <li>Violation des présentes CGU</li>
              <li>Non-paiement de l'abonnement</li>
              <li>Utilisation frauduleuse ou abusive du service</li>
              <li>Demande des autorités compétentes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">12. Modifications des CGU</h2>
            <p className="text-slate-600 leading-relaxed">
              Nous pouvons modifier ces CGU à tout moment. En cas de modification substantielle, 
              nous vous en informerons par email au moins 30 jours avant l'entrée en vigueur des nouvelles conditions. 
              La poursuite de l'utilisation du service vaut acceptation des nouvelles CGU.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">13. Droit applicable et juridiction</h2>
            <p className="text-slate-600 leading-relaxed">
              Les présentes CGU sont régies par le droit français. En cas de litige, 
              et après tentative de résolution amiable, les tribunaux français seront seuls compétents.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">14. Contact</h2>
            <p className="text-slate-600 leading-relaxed">
              Pour toute question concernant ces CGU :
            </p>
            <p className="text-slate-600 mt-4">
              <strong>AI Marketing Engine</strong><br />
              Email : <a href="mailto:contact@ai-marketing-engine.com" className="text-blue-600 hover:underline">contact@ai-marketing-engine.com</a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-6 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600">
          <p>© 2025 AI Marketing Engine. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}
